import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { CustomModalService, Datatype } from '@piqet-angular/shared';

@Component({
  selector: 'piqet-angular-import-issue-resolver',
  templateUrl: './import-issue-resolver.component.html',
  styleUrls: ['./import-issue-resolver.component.scss'],
})
export class ImportIssueResolverComponent {
  modalName: string = 'Import issue resolver';
  modalData: any;
  jsonData: any;
  masterData: any;
  propertiesForm!: FormGroup;
  datatype = Datatype;
  projectId: any;
  controlNameMap: Map<string, string> = new Map();

  @Output() saveData = new EventEmitter<any>();

  constructor(private modal: CustomModalService, private fb: FormBuilder) {
    this.modalData = this.modal?.modalService?.config?.initialState?.['data'];
    this.projectId = this.modal?.modalService?.config?.initialState?.['projectId'];
    this.jsonData = this.modalData.jsonData;
    this.masterData = this.modalData.masterData;
    this.propertiesForm = this.fb.group({});
    this.initForm();
  }

  initForm() {
    this.jsonData.forEach((item: any, index: number) => {
      const uniqueControlName = this.generateUniqueControlName(item.propertyName, index);
      this.controlNameMap.set(this.getItemIdentifier(item, index), uniqueControlName);
    });

    this.jsonData.forEach((item: any, index: number) => {
      const controlName = this.getControlNameForItem(item, index);
      const isRequired = true;

      this.propertiesForm.addControl(
        controlName,
        new FormControl(null, isRequired ? Validators.required : null)
      );
    });
  }

  private getItemIdentifier(item: any, index: number): string {
    return `${index}_${item.propertyName}`;
  }

  getControlNameForItem(item: any, index: number): string {
    const identifier = this.getItemIdentifier(item, index);
    return (
      this.controlNameMap.get(identifier) ||
      this.generateUniqueControlName(item.propertyName, index)
    );
  }

  private getBaseControlName(propertyName: string): string {
    return propertyName.replace(/\s+/g, '').toLowerCase();
  }

  generateUniqueControlName(propertyName: string, index: number): string {
    const baseControlName = this.getBaseControlName(propertyName);
    const isDuplicate = Array.from(this.controlNameMap.values()).includes(baseControlName);
    return isDuplicate ? `${baseControlName}_${index}` : baseControlName;
  }

  getControlName(propertyName: string): string {
    return this.getBaseControlName(propertyName);
  }

  commonInputSearch(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  commonInputSearchForDes(term: string, item: any) {
    return item.description.toLowerCase().includes(term.toLowerCase());
  }

  getDropdownOptions(datatype: number): any[] {
    switch (datatype) {
      case this.datatype.PackagingLevelDropdown:
        return this.masterData.packagingLevelInfo;
      case this.datatype.ProjectShareDropdown:
        return this.masterData.shareItems;
      case this.datatype.ProcessLocationDropdown:
      case this.datatype.DefaultLocationDropdown:
      case this.datatype.MaterialCountryDropdown:
        return this.masterData.regionItems;
      case this.datatype.MaterialDropdown:
        return this.masterData.materialItems;
      case this.datatype.DepartmentDropdown:
        return this.masterData.departmentItems;
      case this.datatype.CategoryDropdown:
        return this.masterData.categoryItems;
      case this.datatype.SubCategoryDropdown:
        return this.masterData.subCategoryItems;
      case this.datatype.SuppliersDropdown:
        return this.masterData.suppliersItems;
      case this.datatype.PackagingConfiguration:
        return this.masterData.packagingLevelInfo;
      default:
        return [];
    }
  }

  isDropdown(datatype: number): boolean {
    return datatype > 1;
  }

  onSearch(term: string, dropdown: any): void {
    dropdown.filter(term);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.propertiesForm.get(fieldName);
    return field !== null && field.invalid && (field.dirty || field.touched);
  }

  getErrorMessage(fieldName: string): string {
    const field = this.propertiesForm.get(fieldName);
    if (field?.errors?.['required']) {
      return 'This field is required';
    }
    return '';
  }

  onClose() {
    this.modal.close();
  }

  onSave() {
    Object.keys(this.propertiesForm.controls).forEach(key => {
      this.propertiesForm.get(key)?.markAsTouched();
    });

    if (this.propertiesForm.valid) {
      const result: any = {};
      this.jsonData.forEach((item: any, index: number) => {
        const controlName = this.getControlNameForItem(item, index);
        item['selectedValue'] =
          this.propertiesForm.get(controlName)?.value?.name ||
          this.propertiesForm.get(controlName)?.value;
        item['selectedId'] = this.propertiesForm.get(controlName)?.value?.id || 0;
      });
      result['id'] = this.projectId;
      result['Updates'] = this.jsonData;
      this.saveData.emit(result);
    }
  }
}
